import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { AmountDisplay, FixedAmountInput, TruncatedAddress, useAddress } from '@onomy/react-hub';
import { OnomyHubConstants } from '@onomy/hub';

import { Caret, Close, Success, EthLogo } from 'components/Modals/Icons';
import * as Modal from 'components/Modals/styles';
import { responsive } from 'theme/constants';
import { useModal } from 'context/modal/ModalContext';
import { MaxBtn, SymbolDisplay } from 'components/Exchange/exchangeStyles';
import { useExchange } from 'context/exchange/ExchangeContext';
import TransactionFailedModal from 'components/Modals/components/TransactionFailedModal';
import { ExplorerButton, TransactionDetailsRow, WalletIcon } from './exchangemodalStyles';
import LoadingSpinner from 'components/UI/LoadingSpinner';

const Message = styled.div`
  margin: 32px 0 0;

  color: ${props => props.theme.colors.textSecondary};

  @media screen and (max-width: ${responsive.smartphone}) {
    font-size: 14px;
  }
`;

const Caption = styled(Modal.Caption)`
  text-align: left;
`;

const ApproveTokensWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 12px;
  margin-top: 24px;

  background-color: ${props => props.theme.colors.bgHighlightBorder};
  border-radius: 8px;

  > div {
    margin-left: 8px;

    display: flex;
    flex-direction: column;
  }

  label {
    margin-bottom: 6px;

    color: ${props => props.theme.colors.textThirdly};
    font-size: 12px;
  }

  input {
    display: block;

    background: none;
    border: none;

    color: ${props => props.theme.colors.textPrimary};
    font-size: 18px;

    &:focus {
      outline: none;
    }
  }
`;

function shorten(addr: string) {
  return `${addr.slice(0, 15)}...${addr.slice(addr.length - 3)}`;
}

export function ApproveSellBridgeNomModal() {
  const [detailsActive, setDetailsActive] = useState(false);
  const { handleModal, onShowConfirmSell } = useModal();
  const [ethAddress] = useAddress(OnomyHubConstants.ETH_ASSET);
  const {
    approveSellTxStatus,
    bNomSellAllowance,
    bNomAskAmount,
    bNomApproveAmount,
    extraAllowanceNeeded,
    doApproveForSale,
    bNomApproveFixed,
    setBNomApproveFixed,
    approveSellReset,
    approveSellTxDetails,
    toggleApproveSellMaxAmount,
  } = useExchange();

  const exploreUrl = useMemo(() => {
    if (!approveSellTxDetails || !ethAddress) return '';
    return `https://${
      ethAddress.chain.name === 'ethereum' ? '' : `${ethAddress.chain.name}.`
    }etherscan.io/tx/${approveSellTxDetails.ethTx.hash}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approveSellTxDetails, ethAddress?.chain.name]);

  function onCloseAfterApprove() {
    onShowConfirmSell();
  }

  useEffect(() => {
    setBNomApproveFixed(extraAllowanceNeeded.amount.toString());
    return () => approveSellReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let infoMessage;
  if (bNomSellAllowance.amount > 0) {
    infoMessage = (
      <Message>
        You have approved the Bonding Curve to sell up to{' '}
        <strong>
          <AmountDisplay balance={bNomSellAllowance} SymbolComponent={SymbolDisplay} />
        </strong>
        . To sell{' '}
        <strong>
          <AmountDisplay balance={bNomAskAmount} SymbolComponent={SymbolDisplay} />
        </strong>
        , you must approve at least an additional{' '}
        <strong>
          <AmountDisplay balance={extraAllowanceNeeded} SymbolComponent={SymbolDisplay} />
        </strong>
        .
      </Message>
    );
  } else {
    infoMessage = (
      <Message>
        You currently have not approved any bNOM for selling. To proceed, you must approve at least{' '}
        <strong>
          <AmountDisplay balance={extraAllowanceNeeded} SymbolComponent={SymbolDisplay} />
        </strong>{' '}
        for selling.
      </Message>
    );
  }

  if (approveSellTxStatus.error) {
    return (
      <TransactionFailedModal
        error={approveSellTxStatus.error}
        header="Approve bNOM for Sale Failed"
      />
    );
  }

  if (approveSellTxStatus.pending) {
    return (
      <Modal.Wrapper>
        <main>
          <Modal.PendingCaption>Waiting for Confirmation</Modal.PendingCaption>

          <Modal.ExchangeResult>
            <Modal.ExchangeResultDescription>You&apos;re approving</Modal.ExchangeResultDescription>
            <AmountDisplay balance={bNomApproveAmount} />
          </Modal.ExchangeResult>
          <TransactionDetailsRow>
            <div>
              <span>Wallet</span>
              <div>
                <strong>{ethAddress ? <TruncatedAddress address={ethAddress} /> : null}</strong>
              </div>
            </div>
            <WalletIcon>
              <EthLogo />
            </WalletIcon>
          </TransactionDetailsRow>
        </main>
        <footer>
          <Modal.LoadingWrapper>
            <LoadingSpinner />
          </Modal.LoadingWrapper>
        </footer>
      </Modal.Wrapper>
    );
  }

  if (approveSellTxStatus.finished) {
    return (
      <Modal.Wrapper>
        <Modal.CloseIcon onClick={() => handleModal()} data-testid="completed-modal-close-icon">
          <Close />
        </Modal.CloseIcon>

        <main>
          <Modal.ModalIconWrapper>
            <Success />
          </Modal.ModalIconWrapper>

          <Modal.Caption>Transaction Confirmed</Modal.Caption>

          <a href={exploreUrl} target="_blank" rel="noreferrer">
            <ExplorerButton>View in Etherscan</ExplorerButton>
          </a>

          <Modal.ExchangeResult data-testid="completed-modal-exchange-result">
            <Modal.ExchangeResultDescription>Approved for selling</Modal.ExchangeResultDescription>
            <AmountDisplay balance={bNomApproveAmount} />
          </Modal.ExchangeResult>
        </main>

        <footer>
          <Modal.FooterControls>
            <Modal.DetailsButton
              // @ts-ignore
              active={detailsActive}
              onClick={() => setDetailsActive(!detailsActive)}
              data-testid="completed-modal-details-button"
            >
              View Details <Caret />
            </Modal.DetailsButton>
            <Modal.PrimaryButton
              onClick={() => onCloseAfterApprove()}
              data-testid="completed-modal-primary-button"
            >
              Next
            </Modal.PrimaryButton>
          </Modal.FooterControls>

          {detailsActive && approveSellTxDetails && (
            <Modal.FooterDetails data-testid="completed-modal-footer-details">
              <Modal.FooterDetailsRow>
                <span>From</span> <strong>{shorten(approveSellTxDetails.ethTx.from)}</strong>
              </Modal.FooterDetailsRow>
              {!!approveSellTxDetails.ethTx.to && (
                <Modal.FooterDetailsRow>
                  <span>To</span> <strong>{shorten(approveSellTxDetails.ethTx.to)}</strong>
                </Modal.FooterDetailsRow>
              )}
              <Modal.FooterDetailsRow>
                <span>TxID</span> <strong>{shorten(approveSellTxDetails.ethTx.hash)}</strong>
              </Modal.FooterDetailsRow>
              <Modal.FooterDetailsRow>
                <span>Network Confirmations</span>{' '}
                <strong>
                  {approveSellTxDetails.ethTx.confirmations > 0
                    ? approveSellTxDetails.ethTx.confirmations
                    : 1}
                </strong>
              </Modal.FooterDetailsRow>

              <a href={exploreUrl} target="_blank" rel="noreferrer">
                <ExplorerButton data-testid="completed-model-explorer-button">
                  View in Explorer
                </ExplorerButton>
              </a>
            </Modal.FooterDetails>
          )}
        </footer>
      </Modal.Wrapper>
    );
  }

  return (
    <Modal.Wrapper>
      <Modal.CloseIcon onClick={() => handleModal()} data-testid="approve-tokens-modal-close-icon">
        <Close />
      </Modal.CloseIcon>

      <main>
        <Caption>Approve Tokens</Caption>

        {infoMessage}

        <ApproveTokensWrapper>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="">Approve tokens (bNOM)</label>
            <FixedAmountInput
              placeholder="0.00"
              value={bNomApproveFixed}
              onChangeValue={setBNomApproveFixed}
            />
          </div>
          <MaxBtn onClick={toggleApproveSellMaxAmount}>MAX</MaxBtn>
        </ApproveTokensWrapper>
      </main>
      <footer>
        <Modal.FooterControls>
          <Modal.SecondaryButton
            onClick={() => handleModal()}
            data-testid="approve-tokens-modal-secondary-button"
          >
            Cancel
          </Modal.SecondaryButton>
          <Modal.PrimaryButton
            onClick={doApproveForSale}
            data-testid="approve-tokens-modal-primary-button"
          >
            Approve
          </Modal.PrimaryButton>
        </Modal.FooterControls>
      </footer>
    </Modal.Wrapper>
  );
}
